import { forwardRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from "react";
import { Slot } from "@radix-ui/react-slot";
import { Eye, EyeOff } from "lucide-react";

type BtnVariant = "gold" | "outline" | "ghost" | "danger";

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; loading?: boolean; asChild?: boolean }
>(({ variant = "gold", loading, asChild, className = "", children, disabled, ...rest }, ref) => {
  const base =
    "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed";
  const styles: Record<BtnVariant, string> = {
    gold: "bg-gradient-gold text-navy-deep shadow-gold hover:translate-y-[-1px]",
    outline: "border border-border hover:border-gold/50 hover:text-gold",
    ghost: "text-muted-foreground hover:text-foreground hover:bg-accent/40",
    danger: "bg-destructive text-destructive-foreground hover:opacity-90",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  if (asChild) {
    return (
      <Slot ref={ref as never} className={cls} {...rest}>
        {children}
      </Slot>
    );
  }
  return (
    <button ref={ref} disabled={disabled || loading} className={cls} {...rest}>
      {loading && (
        <span className="h-4 w-4 rounded-full border-2 border-current border-r-transparent animate-spin" />
      )}
      {children}
    </button>
  );
});
Button.displayName = "Button";

export const Field = ({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) => (
  <label className="block space-y-1.5">
    <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    {children}
    {error ? (
      <span className="block text-xs text-destructive">{error}</span>
    ) : hint ? (
      <span className="block text-xs text-muted-foreground/70">{hint}</span>
    ) : null}
  </label>
);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className = "", ...rest }, ref) => (
    <input
      ref={ref}
      className={`w-full bg-input/60 border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20 transition ${className}`}
      {...rest}
    />
  ),
);
Input.displayName = "Input";

/** Password field with show/hide toggle. */
export const PasswordInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className = "", ...rest }, ref) => {
    const [visible, setVisible] = useState(false);
    return (
      <div className="relative">
        <input
          ref={ref}
          type={visible ? "text" : "password"}
          className={`w-full bg-input/60 border border-border rounded-md px-3 py-2.5 pr-11 text-sm focus:outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20 transition ${className}`}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground"
          aria-label={visible ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`glass rounded-xl p-6 shadow-elev ${className}`}>{children}</div>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <Card className="!p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="mt-2 font-display text-3xl text-gold">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

export function Badge({ tone = "neutral", children }: { tone?: "neutral" | "success" | "warning" | "danger" | "gold"; children: ReactNode }) {
  const tones: Record<string, string> = {
    neutral: "bg-accent text-muted-foreground",
    success: "bg-success/15 text-success",
    warning: "bg-warning/15 text-warning",
    danger: "bg-destructive/15 text-destructive",
    gold: "bg-gold/15 text-gold border border-gold/30",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function ErrorBanner({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {message}
    </div>
  );
}
